import { pick } from 'lodash'
import { ActionTree, ActionContext } from 'vuex'
import {
  fetchJWVideo,
  postVideoStatus,
  fetchVideos,
  fetchVideosForCourse,
  fetchVideosForLearningPath,
  fetchVideosForBootcamp,
  fetchQuizzesForLearningPath,
  fetchExercisesForLearningPath,
  fetchLabsForLearningPath,
  fetchIFramesForLearningPath,
  fetchVideo,
  fetchQuizzes,
  fetchExercises,
  fetchLabs,
  postLabStatus,
  fetchBookmarks,
  postBookmark,
  deleteBookmark,
  fetchCoursesByIds,
  postLegacyStatus,
  postIFrameStatus,
  postMixpanelEvent,
} from '../../../models/content'
import { fetchQuizById } from '../../../models/quizzes'
import { fetchIFrames } from '../../../models/iframes'
import { ContentState } from './state'
import {
  RootState,
  Content,
  Course,
  Group,
  LearningPath,
  Level,
  StringKeys,
} from '../../types'
import { eventsList } from '../../../helpers/eventsList'

const actions: ActionTree<ContentState, RootState> = {
  getJWVideo: async (context: ActionContext<ContentState, {}>, id: string) => {
    try {
      const response = await fetchJWVideo(id)
      context.commit('JW_VIDEO_UPDATED', response)
      return response
    } catch (error) {
      const { message } = error
      if (message === 'You do not have permission to perform this action.') {
        context.dispatch(
          'app/setError',
          'You do not have access to this content.',
          {
            root: true,
          }
        )
      } else {
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  updateVideoStatus: async (
    context: ActionContext<ContentState, {}>,
    videoIdAndStatus: { videoId: string; status: string }
  ) => {
    try {
      await postVideoStatus(videoIdAndStatus)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateLegacyStatus: (
    context: ActionContext<ContentState, {}>,
    userIdAndVideoId: { userId: string; videoId: string }
  ) => {
    postLegacyStatus(userIdAndVideoId)
  },

  getVideoStatus: async (
    context: ActionContext<ContentState, {}>,
    videoId: string
  ) => {
    try {
      const video = await fetchVideo(videoId)
      context.commit('VIDEO_STATUS_UPDATED', video)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getCourseVideos: async (
    context: ActionContext<ContentState, {}>,
    courseAndVideoIds: { courseId: string; videoIds: Array<string> }
  ) => {
    if (courseAndVideoIds.videoIds && courseAndVideoIds.videoIds.length) {
      try {
        const videos = await fetchVideosForCourse(courseAndVideoIds)
        context.commit('CURRENT_VIDEOS_UPDATED', videos)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  getQuizzes: async (
    context: ActionContext<ContentState, {}>,
    quizIds: Array<string>
  ) => {
    if (quizIds && quizIds.length) {
      try {
        const quizzes = await fetchQuizzes(quizIds)
        context.commit('CURRENT_QUIZZES_UPDATED', quizzes)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  getExercises: async (
    context: ActionContext<ContentState, {}>,
    exerciseIds: Array<string>
  ) => {
    if (exerciseIds && exerciseIds.length) {
      try {
        const exercises = await fetchExercises(exerciseIds)
        context.commit('CURRENT_EXERCISES_UPDATED', exercises)
        context.commit('exercises/SET_STUDENT_CODE_EXERCISES', exercises, {
          root: true,
        })
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, {
          root: true,
        })
      }
    }
  },

  getLabs: async (
    context: ActionContext<ContentState, {}>,
    labIds: Array<string>
  ) => {
    if (labIds && labIds.length) {
      try {
        const labs = await fetchLabs(labIds)
        context.commit('CURRENT_LABS_UPDATED', labs)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, {
          root: true,
        })
      }
    }
  },

  updateLabStatus: async (
    context: ActionContext<ContentState, {}>,
    labIdAndStatus: { labId: string; status: string }
  ) => {
    try {
      const userLabStatus = await postLabStatus(labIdAndStatus)
      context.commit('LAB_STATUS_UPDATED', userLabStatus)
      context.commit('labs/LAB_STATUS_UPDATED', userLabStatus, {
        root: true,
      })
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getIFrames: async (
    context: ActionContext<ContentState, {}>,
    iframeIds: Array<string>
  ) => {
    if (iframeIds && iframeIds.length) {
      try {
        const iframes = await fetchIFrames(iframeIds)
        context.commit('CURRENT_IFRAMES_UPDATED', iframes)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, {
          root: true,
        })
      }
    }
  },

  updateIFrameStatus: async (
    context: ActionContext<ContentState, {}>,
    iframeIdAndStatus: { iframeId: string; status: string }
  ) => {
    try {
      await postIFrameStatus(iframeIdAndStatus)
      context.commit('IFRAME_STATUS_UPDATED', iframeIdAndStatus)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getPathVideos: async (
    context: ActionContext<ContentState, {}>,
    pathAndVideoIds: { pathId: string; videoIds: Array<string> }
  ) => {
    if (pathAndVideoIds.videoIds && pathAndVideoIds.videoIds.length) {
      try {
        const videos = await fetchVideosForLearningPath(pathAndVideoIds)
        context.commit('CURRENT_VIDEOS_UPDATED', videos)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },
  getPathQuizzes: async (
    context: ActionContext<ContentState, {}>,
    pathAndQuizIds: { pathId: string; quizIds: Array<string> }
  ) => {
    if (pathAndQuizIds.quizIds && pathAndQuizIds.quizIds.length) {
      try {
        const quizzes = await fetchQuizzesForLearningPath(pathAndQuizIds)
        context.commit('CURRENT_QUIZZES_UPDATED', quizzes)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  getPathLabs: async (
    context: ActionContext<ContentState, {}>,
    pathAndLabIds: { pathId: string; labIds: Array<string> }
  ) => {
    if (pathAndLabIds.labIds && pathAndLabIds.labIds.length) {
      try {
        const labs = await fetchLabsForLearningPath(pathAndLabIds)
        context.commit('CURRENT_LABS_UPDATED', labs)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },
  getPathExercises: async (
    context: ActionContext<ContentState, {}>,
    pathAndExerciseIds: { pathId: string; exerciseIds: Array<string> }
  ) => {
    if (
      pathAndExerciseIds.exerciseIds &&
      pathAndExerciseIds.exerciseIds.length
    ) {
      try {
        const exercises = await fetchExercisesForLearningPath(
          pathAndExerciseIds
        )
        context.commit('CURRENT_EXERCISES_UPDATED', exercises)
        context.commit('exercises/SET_STUDENT_CODE_EXERCISES', exercises, {
          root: true,
        })
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },
  getPathIFrames: async (
    context: ActionContext<ContentState, {}>,
    pathAndIFrameIds: { pathId: string; iframeIds: Array<string> }
  ) => {
    if (pathAndIFrameIds.iframeIds && pathAndIFrameIds.iframeIds.length) {
      try {
        const iframes = await fetchIFramesForLearningPath(pathAndIFrameIds)
        context.commit('CURRENT_IFRAMES_UPDATED', iframes)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  getQuizStatus: async (
    context: ActionContext<ContentState, {}>,
    quizId: string
  ) => {
    try {
      const quiz = await fetchQuizById(quizId)
      context.commit('QUIZ_STATUS_UPDATED', quiz)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getBookmarks: async (context: ActionContext<ContentState, {}>) => {
    try {
      const bookmarks = await fetchBookmarks()
      context.commit('CURRENT_BOOKMARKS_UPDATED', bookmarks)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  addBookmark: async (
    context: ActionContext<ContentState, {}>,
    bookmark: object
  ) => {
    try {
      await postBookmark(bookmark)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  removeBookmark: async (
    context: ActionContext<ContentState, {}>,
    id: string
  ) => {
    try {
      await deleteBookmark(id)
      context.dispatch('app/setNotification', 'The bookmark was deleted.', {
        root: true,
      })
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getCoursesForBookmarks: async (
    context: ActionContext<ContentState, {}>,
    courseIds: Array<string>
  ) => {
    try {
      const courses = await fetchCoursesByIds(courseIds)
      context.commit('COURSES_FOR_BOOKMARKS_UPDATED', courses)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getVideosForBookmarks: async (
    context: ActionContext<ContentState, {}>,
    videoIds: Array<string>
  ) => {
    try {
      const videos = await fetchVideos(videoIds)
      context.commit('VIDEOS_FOR_BOOKMARKS_UPDATED', videos)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  setPlayingVideoId: (
    context: ActionContext<ContentState, {}>,
    videoId: String
  ) => {
    context.commit('PLAYING_VIDEO_ID_UPDATED', videoId)
  },

  setPlayingVideoTime: (
    context: ActionContext<ContentState, {}>,
    time: number
  ) => {
    context.commit('PLAYING_VIDEO_TIME_UPDATED', time)
  },

  setIsWatchingFullscreen: (
    context: ActionContext<ContentState, {}>,
    isWatchingFullscreen: boolean
  ) => {
    context.commit('IS_WATCHING_FULLSCREEN_UPDATED', isWatchingFullscreen)
  },

  setCurrentQuizId: (
    context: ActionContext<ContentState, {}>,
    quizId: string
  ) => {
    context.commit('CURRENT_QUIZ_ID_UPDATED', quizId)
  },

  setIsContentLoading: (
    context: ActionContext<ContentState, {}>,
    isLoading: boolean
  ) => {
    context.commit('IS_CONTENT_LOADING_UPDATED', isLoading)
  },

  setCurrentHoveredNote: (
    context: ActionContext<ContentState, {}>,
    id: string
  ) => {
    context.commit('CURRENT_HOVERED_NOTE_UPDATED', id)
  },

  sendMixpanelEvent: async (
    context: ActionContext<ContentState, RootState>,
    payload: { event_name: string; properties: object }
  ) => {
    if (!process.env.VUE_APP_MIXPANEL_ENABLED) return
    try {
      const sentPayload = {
        user_id: context.rootState.auth.userProfile.uaaId,
        event_name: payload.event_name,
        properties: {
          source: 'my.ine.com',
          starter_pass: context.rootState.auth.subscriptionStatus,
          ...pick(payload.properties, eventsList[payload.event_name]),
        },
      }
      await postMixpanelEvent(sentPayload)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  fetchContentForCourse: async (
    context: ActionContext<ContentState, RootState>,
    payload: { course: Course; isAuthenticated: boolean }
  ) => {
    const { course, isAuthenticated } = payload
    const gatherContentIds = () => {
      const ids: StringKeys = {}
      course.content.map(group =>
        group.content.map(level =>
          level.content.map(content => {
            const { content_type: type, uuid } = content
            if (ids[type] !== undefined) {
              ids[type].push(uuid)
            } else {
              ids[type] = [uuid]
            }
          })
        )
      )
      return ids
    }
    const { video, lab, iframe, quiz, exercise } = gatherContentIds()
    await context.dispatch('getCourseVideos', {
      videoIds: video,
      courseId: course.id,
    })
    await context.dispatch('getLabs', lab)
    await context.dispatch('getIFrames', iframe)
    if (isAuthenticated) {
      await context.dispatch('getQuizzes', quiz)
      await context.dispatch('getExercises', exercise)
    }
  },

  fetchContentForLearningPath: async (
    context: ActionContext<ContentState, RootState>,
    payload: { learningPath: LearningPath; isAuthenticated: boolean }
  ) => {
    const { learningPath, isAuthenticated } = payload
    const ids: { [key: string]: any } = {
      video: [],
      quiz: [],
      exercise: [],
      lab: [],
      iframe: [],
    }
    if (learningPath.content) {
      learningPath.content.map(group =>
        group.content.map(level => {
          if (ids[level.content_type]) {
            ids[level.content_type].push(level.uuid)
          }
        })
      )
    }
    if (ids.video.length) {
      await context.dispatch('getPathVideos', {
        videoIds: ids.video,
        pathId: learningPath.id,
      })
    }
    if (ids.quiz.length && isAuthenticated) {
      await context.dispatch('getPathQuizzes', {
        quizIds: ids.quiz,
        pathId: learningPath.id,
      })
    }
    if (ids.lab.length) {
      await context.dispatch('getPathLabs', {
        labIds: ids.lab,
        pathId: learningPath.id,
      })
    }
    if (ids.exercise.length && isAuthenticated) {
      await context.dispatch('getPathExercises', {
        exerciseIds: ids.exercise,
        pathId: learningPath.id,
      })
    }
    if (ids.iframe.length) {
      await context.dispatch('getPathIFrames', {
        iframeIds: ids.iframe,
        pathId: learningPath.id,
      })
    }
  },

  getBootcampVideos: async (
    context: ActionContext<ContentState, RootState>,
    ids
  ) => {
    if (ids && ids.length) {
      try {
        const videos = await fetchVideosForBootcamp(ids)
        context.commit('CURRENT_VIDEOS_UPDATED', videos)
      } catch (error) {
        const { message } = error
        context.dispatch('app/setError', message, { root: true })
      }
    }
  },

  fetchContentForBootcamp: async (
    context: ActionContext<ContentState, RootState>,
    payload: { content: Array<Group>; isAuthenticated: boolean }
  ) => {
    const {
      content: bootcampContent,
      isAuthenticated,
    }: { content: Array<Group>; isAuthenticated: boolean } = payload
    const gatherContentIds = () => {
      const ids: StringKeys = {}
      bootcampContent.map((group: Group) =>
        group.content.map((level: Level) =>
          level.content.map((content: Content) => {
            const { content_type: type, uuid } = content
            if (ids[type] !== undefined) {
              ids[type].push(uuid)
            } else {
              ids[type] = [uuid]
            }
          })
        )
      )
      return ids
    }
    const { video, quiz } = gatherContentIds()
    await context.dispatch('getBootcampVideos', video)
    if (isAuthenticated) {
      await context.dispatch('getQuizzes', quiz)
    }
  },

  resetContentState: (context: ActionContext<ContentState, RootState>) => {
    context.dispatch('setPlayingVideoId', '')
    context.dispatch('setPlayingVideoTime', 0)
    context.dispatch('setIsWatchingFullscreen', false)
    context.dispatch('setCurrentQuizId', '')
    context.dispatch('setCurrentHoveredNote', '')
  },
}

export default actions
